import { zodResolver } from "@hookform/resolvers/zod";
import { Check } from "@tamagui/lucide-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useController, useForm } from "react-hook-form";
import { Masks } from "react-native-mask-input";
import { z } from "zod";

import {
  getGetAgeVerificationStatusQueryKey,
  useUpdateUserProfile,
} from "@/api/mutations/";
import type { UserProfileFormData } from "@/api/mutations/userProfile";
import {
  Adapt,
  Button,
  Dialog,
  H1,
  MaskedInput,
  Sheet,
  XStack,
  YStack,
} from "@/components";
import { Text } from "@/components/Typography";
import { useAgeVerificationStore } from "@/store/ageVerification";
import {
  validateDate,
  validateDateInPast,
  validateMaxAge,
  validateMmDdYyyyFormat,
} from "@/utils/validators";

const ageVerificationSchema = z.object({
  dateOfBirth: z
    .string()
    .refine((value) => value.trim() !== "", {
      message: "Date of birth is required",
    })
    .refine(validateMmDdYyyyFormat, {
      message: "Please use mm/dd/yyyy with a 4-digit year",
    })
    .refine((value) => validateDate(value), {
      message: "Invalid date",
    })
    .refine((value) => validateDateInPast(value), {
      message: "Date must be in the past",
    })
    .refine((value) => validateMaxAge(value), {
      message: "Please enter a valid date of birth",
    }),
});

type AgeVerificationSchemaType = z.infer<typeof ageVerificationSchema>;

export default function AgeVerificationDialog() {
  const { userDateOfBirthRequested, clearUserDateOfBirthRequested } =
    useAgeVerificationStore();
  const { mutate: updateUserProfile, isPending: isUpdating } =
    useUpdateUserProfile();
  const queryClient = useQueryClient();

  const { control, handleSubmit } = useForm<AgeVerificationSchemaType>({
    resolver: zodResolver(ageVerificationSchema),
    mode: "onBlur",
    defaultValues: {
      dateOfBirth: "",
    },
  });

  const {
    field: dateOfBirthField,
    fieldState: { error: dateOfBirthError },
  } = useController({ control, name: "dateOfBirth" });

  const handleConfirm = handleSubmit((formData) => {
    updateUserProfile(
      { data: { dateOfBirth: formData.dateOfBirth } as UserProfileFormData },
      {
        onSuccess: () => {
          clearUserDateOfBirthRequested();
          queryClient.invalidateQueries({
            queryKey: getGetAgeVerificationStatusQueryKey(),
          });
        },
      },
    );
  });

  const handleOverlayPress = (event: any) => {
    event.stopPropagation();
    event.preventDefault?.();
    return false;
  };

  const dialogContent = (
    <YStack
      flex={1}
      padding="$300"
      gap="$200"
      justifyContent="space-between"
      $lg={{ justifyContent: "flex-start" }}
    >
      <YStack gap="$200" marginTop="$300" $lg={{ gap: "$150", marginTop: 0 }}>
        <H1 size="$xl" $lg={{ size: "$xl" }}>
          Confirm your date of birth
        </H1>
        <Text>
          It seems like you might be under 18, and we need to verify your date
          of birth.
        </Text>
        <MaskedInput.Controlled
          label="Date of birth"
          value={dateOfBirthField.value ?? ""}
          onChangeText={dateOfBirthField.onChange}
          inputMode="numeric"
          onBlur={dateOfBirthField.onBlur}
          mask={Masks.DATE_MMDDYYYY}
          placeholder="mm/dd/yyyy"
          errorText={dateOfBirthError?.message}
        />
      </YStack>

      <XStack
        justifyContent="flex-end"
        $lg={{ marginTop: "auto", gap: "$100" }}
      >
        <Button
          mode="primary"
          onPress={handleConfirm}
          icon={<Check />}
          width="100%"
          $lg={{ width: "auto" }}
          loading={isUpdating}
        >
          Confirm
        </Button>
      </XStack>
    </YStack>
  );

  return (
    <Dialog open={userDateOfBirthRequested} modal disableRemoveScroll>
      <Dialog.Adapt when="maxLg">
        <Sheet
          modal
          disableDrag
          dismissOnSnapToBottom={false}
          snapPoints={[98]}
        >
          <Sheet.Frame flex={1}>
            <Adapt.Contents />
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            backdropFilter="blur(8px)"
            onPress={handleOverlayPress}
          />
        </Sheet>
      </Dialog.Adapt>
      <Dialog.Portal>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
          onPress={handleOverlayPress}
        />
        <Dialog.FocusScope trapped loop>
          <Dialog.Content
            borderRadius="$150"
            minWidth={400}
            maxWidth={500}
            flexDirection="column"
          >
            {dialogContent}
          </Dialog.Content>
        </Dialog.FocusScope>
      </Dialog.Portal>
    </Dialog>
  );
}
